import React, { useState } from 'react';
import { EstateAssets, EstateProfile } from '../../types';
import { HelpPopover } from '../HelpPopover';
import { HELP_CONTENT } from '../helpContent';
import { Fieldset, RadioGroup, NumericInput, TextInput } from '../ui';

const COMMON_COUNTRIES = [
    'France', 'Spain', 'Italy', 'Portugal', 'USA', 'Germany', 'Ireland',
    'Australia', 'Canada', 'South Africa', 'Greece', 'Cyprus', 'Turkey',
    'UAE', 'India', 'New Zealand', 'Switzerland', 'Netherlands', 'Belgium',
];

const COUNTRIES_WITH_OWN_TAX = ['France', 'Spain', 'Italy', 'Portugal', 'USA'];

const VALUE_RANGES: { key: NonNullable<EstateAssets['foreign_assets']>['value_range']; label: string; midpoint: number }[] = [
    { key: 'under_50k', label: 'Under £50,000', midpoint: 25000 },
    { key: '50k_250k', label: '£50,000 – £250,000', midpoint: 150000 },
    { key: '250k_1m', label: '£250,000 – £1,000,000', midpoint: 625000 },
    { key: 'over_1m', label: 'Over £1,000,000', midpoint: 1500000 },
    { key: 'unsure', label: 'Unsure', midpoint: 0 },
];

const ASSET_TYPE_OPTIONS: { key: NonNullable<NonNullable<EstateAssets['foreign_assets']>['asset_types']>[number]; label: string }[] = [
    { key: 'property', label: 'Property' },
    { key: 'bank_accounts', label: 'Bank accounts' },
    { key: 'investments', label: 'Investments' },
    { key: 'business', label: 'Business interests' },
    { key: 'other', label: 'Other / unsure' },
];

interface Props {
    assets: EstateAssets;
    onChange: (assets: EstateAssets) => void;
    profile: EstateProfile;
    onProfileChange: (profile: EstateProfile) => void;
}

export const ComplexityStep: React.FC<Props> = ({ assets, onChange, profile, onProfileChange }) => {
    const fa = assets.foreign_assets;
    const trustScreening = profile.iht_details?.trust_screening || {};
    const deceased = profile.deceased || {};

    // Discovery-step gate. ComplexityStep asks trust *details* only when the
    // user said Yes or Not sure at Discovery (§2 routing flag).
    const trustsFlagged =
        assets.trusts_or_life_interest?.exists === true ||
        assets.trusts_or_life_interest?.unsure === true;

    const updateDeceased = (field: string, value: any) => {
        onProfileChange({
            ...profile,
            deceased: { ...deceased, [field]: value },
        });
    };

    const updateTrustScreening = (next: Partial<NonNullable<EstateProfile['iht_details']>['trust_screening']>) => {
        onProfileChange({
            ...profile,
            iht_details: {
                ...(profile.iht_details || {}),
                trust_screening: { ...trustScreening, ...next },
            },
        });
    };
    const [showExactValue, setShowExactValue] = useState(fa?.exact_value_entered === true);
    const [countryInput, setCountryInput] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);

    const updateFA = (updates: Record<string, any>) => {
        const current = assets.foreign_assets || {};
        onChange({ ...assets, foreign_assets: { ...current, ...updates } as any });
    };

    const toggleAssetType = (
        type: NonNullable<NonNullable<EstateAssets['foreign_assets']>['asset_types']>[number]
    ) => {
        const current = fa?.asset_types ?? [];
        const next = current.includes(type)
            ? current.filter(t => t !== type)
            : [...current, type];
        const hasBusiness = next.includes('business');
        updateFA({
            asset_types: next,
            business_interest_flag: hasBusiness || null,
            needs_review: hasBusiness || null,
            ...(!hasBusiness ? { business_is_trading: null } : {}),
        });
    };

    const addCountry = (country: string) => {
        const trimmed = country.trim();
        if (!trimmed) return;
        const current = fa?.countries ?? [];
        if (!current.includes(trimmed)) {
            updateFA({ countries: [...current, trimmed] });
        }
        setCountryInput('');
        setShowCountryDropdown(false);
    };

    const removeCountry = (country: string) => {
        const current = fa?.countries ?? [];
        updateFA({ countries: current.filter(c => c !== country) });
    };

    const filteredCountries = COMMON_COUNTRIES.filter(c =>
        c.toLowerCase().includes(countryInput.toLowerCase()) &&
        !(fa?.countries ?? []).includes(c)
    );

    const hasOwnTaxCountries = (fa?.countries ?? []).filter(c =>
        COUNTRIES_WITH_OWN_TAX.includes(c)
    );

    // Helpers to map yes/no/null to the radio's string value space
    const ynuValue = (b: boolean | null | undefined): string =>
        b === true ? 'yes' : b === false ? 'no' : '';
    const ynuSet = (v: string): boolean | null =>
        v === 'yes' ? true : v === 'no' ? false : null;

    return (
        <div className="max-w-[640px] mx-auto">
            <p className="font-serif italic text-[15px] text-ink2 mb-10 leading-relaxed">
                A few flags that can change the audit pathway. Answering ‘unsure’ is
                fine — we'll mark it for review.
            </p>

            {/* §7C — Origin signal (deemed-domicile / long-residence hint) */}
            <section className="mb-12">
                <h3 className="font-serif text-[17px] text-ink mb-4">Origin</h3>
                <Fieldset
                    label="Was the deceased originally from outside the UK?"
                    help="Helps flag whether deemed-domicile or long-residence rules need a closer look. Doesn't override the domicile answer in Basics."
                >
                    <RadioGroup
                        name="origin_outside_uk"
                        value={deceased.origin_outside_uk || ''}
                        onChange={(v) => updateDeceased('origin_outside_uk', (v || null) as any)}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: 'unsure', label: 'Not sure' },
                        ]}
                    />
                </Fieldset>
            </section>

            {/* Foreign Assets */}
            <section className="mb-12">
                <h3 className="font-serif text-[17px] text-ink mb-4 flex items-baseline gap-2">
                    Foreign assets
                    <HelpPopover content={HELP_CONTENT.FOREIGN_ASSETS} />
                </h3>

                <Fieldset
                    label="Did the deceased own assets outside the UK?"
                    help={fa?.exists === true
                        ? 'Foreign assets often require a separate grant of probate in that country.'
                        : undefined}
                >
                    <RadioGroup
                        name="foreign_exists"
                        value={ynuValue(fa?.exists)}
                        onChange={(v) => {
                            const b = ynuSet(v);
                            if (b === true) {
                                updateFA({ exists: true });
                            } else {
                                updateFA({
                                    exists: b,
                                    asset_types: null,
                                    value_estimate: null,
                                    value_range: null,
                                    countries: null,
                                    exact_value_entered: null,
                                });
                                setShowExactValue(false);
                            }
                        }}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: '', label: 'Unsure' },
                        ]}
                    />
                </Fieldset>

                {fa?.exists === true && (
                    <div className="pl-4 border-l border-rule space-y-8 mt-2">
                        {/* Asset types — multi-select chips */}
                        <Fieldset
                            label="Type of foreign assets"
                            help="Select all that apply."
                        >
                            <div className="flex flex-wrap gap-2">
                                {ASSET_TYPE_OPTIONS.map(({ key, label }) => {
                                    const selected = (fa?.asset_types ?? []).includes(key);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => toggleAssetType(key)}
                                            className={`px-3 py-1.5 border text-[12px] transition-colors
                                                ${selected
                                                    ? 'bg-accent-soft border-accent text-accent font-semibold'
                                                    : 'bg-white border-rule text-ink2 hover:border-sub'}
                                            `}
                                        >
                                            {selected && <span className="mr-1">✓</span>}
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </Fieldset>

                        {(fa?.asset_types ?? []).includes('business') && (
                            <>
                                <p className="font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent">
                                    Foreign business interests may need specialist review — relief
                                    treatment depends on whether the business is trading or
                                    investment.
                                </p>
                                <Fieldset
                                    label="Were any of these foreign business interests trading businesses?"
                                    help="Trading businesses may qualify for Business Property Relief."
                                >
                                    <RadioGroup
                                        name="business_trading"
                                        value={ynuValue(fa?.business_is_trading)}
                                        onChange={(v) => updateFA({ business_is_trading: ynuSet(v) })}
                                        inline
                                        options={[
                                            { value: 'yes', label: 'Yes' },
                                            { value: 'no', label: 'No' },
                                            { value: '', label: 'Not sure' },
                                        ]}
                                    />
                                </Fieldset>
                            </>
                        )}

                        {/* Value estimate */}
                        <Fieldset
                            label="Approximate total value of foreign assets"
                            help="A range is fine for the first estimate. Switch to an exact figure if known."
                        >
                            {!showExactValue ? (
                                <>
                                    <RadioGroup
                                        name="foreign_value_range"
                                        value={fa?.value_range ?? ''}
                                        onChange={(v) => {
                                            const range = VALUE_RANGES.find(r => r.key === v);
                                            updateFA({
                                                value_range: v as any,
                                                value_estimate: v === 'unsure' ? null : range?.midpoint ?? null,
                                                exact_value_entered: false,
                                            });
                                        }}
                                        options={VALUE_RANGES.map(r => ({
                                            value: r.key as string,
                                            label: r.label,
                                        }))}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowExactValue(true)}
                                        className="mt-2 text-[11px] text-accent hover:text-accent-hover underline decoration-accent-rule underline-offset-4"
                                    >
                                        I know the exact value
                                    </button>
                                </>
                            ) : (
                                <>
                                    <NumericInput
                                        type="number"
                                        min={0}
                                        placeholder="£0"
                                        value={fa?.value_estimate ?? ''}
                                        onChange={(e) => updateFA({
                                            value_estimate: e.target.value ? Number(e.target.value) : null,
                                            value_range: null,
                                            exact_value_entered: true,
                                        })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowExactValue(false)}
                                        className="mt-2 text-[11px] text-accent hover:text-accent-hover underline decoration-accent-rule underline-offset-4"
                                    >
                                        Use a range instead
                                    </button>
                                </>
                            )}
                        </Fieldset>

                        {/* Country picker */}
                        <Fieldset
                            label="Which country were the assets located in?"
                            help="Start typing to pick from common countries, or enter any country name."
                        >
                            {(fa?.countries ?? []).length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {(fa?.countries ?? []).map(c => (
                                        <span key={c} className="inline-flex items-center gap-2 px-3 py-1.5 border border-accent bg-accent-soft text-[12px] text-accent font-semibold">
                                            {c}
                                            <button
                                                type="button"
                                                onClick={() => removeCountry(c)}
                                                className="text-accent hover:text-accent-hover"
                                                aria-label={`Remove ${c}`}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="relative">
                                <TextInput
                                    type="text"
                                    placeholder="e.g. Spain"
                                    value={countryInput}
                                    onChange={(e) => {
                                        setCountryInput(e.target.value);
                                        setShowCountryDropdown(true);
                                    }}
                                    onFocus={() => setShowCountryDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (filteredCountries.length > 0) {
                                                addCountry(filteredCountries[0]);
                                            } else if (countryInput.trim()) {
                                                addCountry(countryInput);
                                            }
                                        }
                                    }}
                                />
                                {showCountryDropdown && countryInput.length > 0 && filteredCountries.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border border-rule max-h-40 overflow-y-auto">
                                        {filteredCountries.slice(0, 8).map(c => (
                                            <button
                                                key={c}
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-[13px] text-ink2 hover:bg-paper transition-colors"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    addCountry(c);
                                                }}
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {hasOwnTaxCountries.length > 0 && (
                                <p className="mt-3 font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent">
                                    <strong className="font-semibold not-italic">{hasOwnTaxCountries.join(', ')}</strong>{' '}
                                    {hasOwnTaxCountries.length === 1 ? 'has its' : 'have their'} own
                                    inheritance or estate tax rules which may apply separately. This tool
                                    does not calculate foreign taxes.
                                </p>
                            )}
                        </Fieldset>

                        {(fa?.value_estimate ?? 0) > 0 && (
                            <p className="text-[12px] text-ink3 leading-relaxed">
                                Foreign assets of approximately{' '}
                                <span className="font-mono text-ink">
                                    £{(fa?.value_estimate ?? 0).toLocaleString('en-GB')}
                                </span>{' '}
                                will be included in the estate total for UK IHT purposes
                                {fa?.exact_value_entered ? '' : ' (based on the selected range)'}.
                            </p>
                        )}
                    </div>
                )}
            </section>

            {/* §7A — Trust details (gated on Discovery routing flag) */}
            {trustsFlagged && (
                <section className="border-t border-rule pt-12 mb-12">
                    <h3 className="font-serif text-[17px] text-ink mb-2">Trusts — detail</h3>
                    <p className="font-serif italic text-[14px] text-ink2 mb-6 leading-relaxed">
                        You flagged trusts at the Discovery step. A few follow-ups so we
                        can route the IHT calculation correctly.
                    </p>

                    <Fieldset label="Did the deceased create or transfer assets into any trusts?">
                        <RadioGroup
                            name="trust_created_or_transferred"
                            value={ynuValue(trustScreening.created_or_transferred_trust)}
                            onChange={(v) => updateTrustScreening({ created_or_transferred_trust: ynuSet(v) })}
                            inline
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                                { value: '', label: 'Not sure' },
                            ]}
                        />
                    </Fieldset>

                    <Fieldset label="Did the deceased benefit from a trust during their lifetime?">
                        <RadioGroup
                            name="trust_benefited_from"
                            value={ynuValue(trustScreening.benefited_from_trust)}
                            onChange={(v) => updateTrustScreening({ benefited_from_trust: ynuSet(v) })}
                            inline
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                                { value: '', label: 'Not sure' },
                            ]}
                        />
                    </Fieldset>

                    <Fieldset
                        label="Did they give away assets but continue to use or benefit from them?"
                        help="Gift-with-reservation. Affects whether the asset still counts in the estate."
                    >
                        <RadioGroup
                            name="trust_gwr"
                            value={ynuValue(trustScreening.has_gift_with_reservation)}
                            onChange={(v) => updateTrustScreening({ has_gift_with_reservation: ynuSet(v) })}
                            inline
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' },
                                { value: '', label: 'Not sure' },
                            ]}
                        />
                    </Fieldset>

                    {assets.trusts_or_life_interest?.unsure === true && (
                        <p className="font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent">
                            You answered "Not sure" at Discovery. Anything you can confirm
                            here will sharpen the audit; otherwise we'll flag it.
                        </p>
                    )}
                </section>
            )}

            {/* §7B — Life Insurance (reworded to foreground the trust angle) */}
            <section className="border-t border-rule pt-12">
                <h3 className="font-serif text-[17px] text-ink mb-4">Life insurance</h3>

                <Fieldset
                    label="Do you know if any life insurance policies were written in trust?"
                    help="Policies written in trust pay directly to beneficiaries and are excluded from the estate for IHT. If unsure, we'll flag it."
                >
                    <RadioGroup
                        name="life_insurance_exists"
                        value={ynuValue(assets.life_insurance?.exists)}
                        onChange={(v) => {
                            const b = ynuSet(v);
                            const li = assets.life_insurance || { exists: null };
                            if (b === true) {
                                onChange({ ...assets, life_insurance: { ...li, exists: true } });
                            } else {
                                onChange({
                                    ...assets,
                                    life_insurance: { exists: b, written_in_trust: null, estimated_value: null },
                                });
                            }
                        }}
                        inline
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                            { value: '', label: 'Unsure' },
                        ]}
                    />
                </Fieldset>

                {assets.life_insurance?.exists === true && (
                    <div className="pl-4 border-l border-rule space-y-8 mt-2">
                        <Fieldset
                            label="Were the policies written in trust?"
                            help="If written in trust, the payout goes directly to beneficiaries and is not part of the estate for IHT."
                        >
                            <RadioGroup
                                name="life_in_trust"
                                value={ynuValue(assets.life_insurance?.written_in_trust)}
                                onChange={(v) => onChange({
                                    ...assets,
                                    life_insurance: { ...assets.life_insurance!, written_in_trust: ynuSet(v) },
                                })}
                                inline
                                options={[
                                    { value: 'yes', label: 'Yes, in trust' },
                                    { value: 'no', label: 'No, not in trust' },
                                    { value: '', label: 'Unsure' },
                                ]}
                            />
                        </Fieldset>

                        {assets.life_insurance?.written_in_trust !== true && (
                            <Fieldset
                                label="Estimated payout value"
                                help={
                                    assets.life_insurance?.written_in_trust === false
                                        ? 'This will be included in the estate for IHT purposes.'
                                        : 'If unsure about trust status, we include it as a cautious estimate.'
                                }
                            >
                                <NumericInput
                                    type="number"
                                    min={0}
                                    placeholder="£0"
                                    value={assets.life_insurance?.estimated_value ?? ''}
                                    onChange={(e) => onChange({
                                        ...assets,
                                        life_insurance: {
                                            ...assets.life_insurance!,
                                            estimated_value: e.target.value ? Number(e.target.value) : null,
                                        },
                                    })}
                                />
                            </Fieldset>
                        )}

                        {assets.life_insurance?.written_in_trust === true && (
                            <p className="font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent">
                                Policies written in trust are paid directly to beneficiaries and are
                                <strong className="font-semibold not-italic"> not included</strong> in
                                the estate for IHT purposes.
                            </p>
                        )}
                    </div>
                )}

                {assets.life_insurance?.exists === null && assets.life_insurance !== undefined && (
                    <p className="mt-3 font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent">
                        Check for any life insurance documents or contact the deceased's
                        financial adviser to confirm.
                    </p>
                )}
            </section>
        </div>
    );
};
